/**
 * @fileoverview DateTime widget — ISO 8601 string.
 * @module inspection-renderer/fields
 *
 * Responsibilities:
 * - Render <input type="datetime-local"> with offset-based min/max and
 *   step-minute enforcement from template extras configuration.
 * - Resolve 'now' sentinel for defaultValue and min/max at mount.
 * - Validate year range to prevent nonsensical dates (e.g. year 275760).
 * - Snap time to the configured step interval on change.
 *
 * @component DateTimeField
 */
import { useEffect, useMemo } from 'react';
import { FieldWrapper } from './FieldWrapper';
import type { BaseWidgetProps } from './widgetTypes';
import { resolveFieldDefault } from './fieldDefaults';

/** Extras shape stored by the template-builder PropertiesPanel for dateTime fields. */
interface DateTimeExtras {
  min?: string;
  max?: string;
  defaultToNow?: boolean;
  minDateOffsetDays?: number;
  maxDateOffsetDays?: number;
  stepMinutes?: number;
  timeZoneMode?: string;
}

const pad = (n: number) => String(n).padStart(2, '0');

export function nowLocalDatetime(): string {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function resolveNowSentinel(raw: string | undefined): string | undefined {
  if (raw === 'now') return nowLocalDatetime();
  return raw;
}

/**
 * Compute a local datetime string offset from today by `days`.
 * Uses local midnight for the date portion and 00:00 / 23:59 for the time
 * portion depending on direction (min uses 00:00, max uses 23:59).
 */
function offsetFromToday(days: number, edge: 'start' | 'end'): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  const time = edge === 'start' ? '00:00' : '23:59';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${time}`;
}

/**
 * Snap a datetime-local value to the nearest step interval.
 * E.g. with step=5, "2026-05-29T17:53" → "2026-05-29T17:55".
 */
function snapToStep(value: string, stepMinutes: number): string {
  if (stepMinutes <= 1) return value;
  const match = value.match(/^(\d{4}-\d{2}-\d{2}T\d{2}):(\d{2})$/);
  if (!match) return value;
  const minutes = parseInt(match[2], 10);
  const snapped = Math.round(minutes / stepMinutes) * stepMinutes;
  // Handle overflow (e.g. 58 snapped to 60 → next hour)
  if (snapped >= 60) {
    const d = new Date(`${match[1]}:00`);
    d.setMinutes(snapped);
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }
  return `${match[1]}:${pad(snapped)}`;
}

/** Validate year range — reject dates with unreasonable years. */
function isValidDateTimeYear(value: string): boolean {
  const year = parseInt(value.slice(0, 4), 10);
  return !isNaN(year) && year >= 1900 && year <= 2100;
}

/** Format a `YYYY-MM-DDTHH:mm` string into the user's locale. */
function formatDateTimeLocale(local: string): string {
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return local;
  try {
    return d.toLocaleString();
  } catch {
    return local;
  }
}

export function DateTimeField(props: BaseWidgetProps<string | null | undefined>) {
  const { field, value, isNA, resolved, onChange, onToggleNA, errorMessage, requiredUnmet } = props;
  const extras = (field.extras ?? {}) as DateTimeExtras;

  // Compute effective min/max: offset-based takes precedence over raw string.
  const effectiveMin = useMemo(() => {
    if (typeof extras.minDateOffsetDays === 'number') return offsetFromToday(extras.minDateOffsetDays, 'start');
    return resolveNowSentinel(extras.min);
  }, [extras.minDateOffsetDays, extras.min]);

  const effectiveMax = useMemo(() => {
    if (typeof extras.maxDateOffsetDays === 'number') return offsetFromToday(extras.maxDateOffsetDays, 'end');
    return resolveNowSentinel(extras.max);
  }, [extras.maxDateOffsetDays, extras.max]);

  const stepMinutes = extras.stepMinutes ?? 1;
  // HTML5 step attribute for datetime-local is in seconds.
  const stepSeconds = stepMinutes > 1 ? stepMinutes * 60 : undefined;

  // Resolve `defaultValue === 'now'` at mount.
  useEffect(() => {
    if (value != null && value !== '') return;
    const dv = resolveFieldDefault(field);
    if (dv === 'now') {
      const now = nowLocalDatetime();
      onChange(stepMinutes > 1 ? snapToStep(now, stepMinutes) : now);
    } else if (typeof dv === 'string' && dv) {
      onChange(dv);
    }

  }, [field.id]);

  /** Validated + snapped onChange handler. */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (raw === '') {
      onChange(null);
      return;
    }
    // Reject invalid years (e.g. year 275760 from keyboard entry)
    if (!isValidDateTimeYear(raw)) return;
    // Enforce min/max bounds (soft clamp — browser may not enforce on keyboard entry)
    if (effectiveMin && raw < effectiveMin) return;
    if (effectiveMax && raw > effectiveMax) return;
    // Snap to step interval
    const snapped = stepMinutes > 1 ? snapToStep(raw, stepMinutes) : raw;
    onChange(snapped);
  };

  return (
    <FieldWrapper
      fieldId={field.id}
      widgetType="dateTime"
      label={field.label}
      helpText={field.helpText}
      required={field.required}
      resolved={resolved}
      isNA={isNA}
      naEnabled={field.naEnabled}
      onToggleNA={onToggleNA}
      errorMessage={errorMessage}
      requiredUnmet={requiredUnmet} requiredImmediate={props.requiredImmediate}
    >
      <input
        id={field.id}
        type="datetime-local"
        value={value ?? ''}
        onChange={handleChange}
        min={effectiveMin}
        max={effectiveMax}
        step={stepSeconds}
        disabled={!!resolved?.disabled}
        aria-required={!!(field.required || resolved?.required)}
        aria-invalid={!!errorMessage}
        data-formatted={value ? formatDateTimeLocale(value) : ''}
        className="w-full px-3 py-2 text-sm border border-slate-300 rounded-sm outline-hidden focus:ring-2 focus:ring-orange-300 disabled:opacity-50"
      />
    </FieldWrapper>
  );
}
